/**
 * First-party analytics helpers (Phase 1). Pure functions only — nothing here sends data.
 * Client tracking stays OFF until separately approved (see featureFlags.ANALYTICS_*).
 *
 * Privacy: random visitor/session ids (not linked to accounts), no IP, no user agent,
 * referrer reduced to its domain, query strings never stored, opt-out + Global Privacy
 * Control honored. Whether a consent banner is legally required is a counsel decision
 * per jurisdiction and is deliberately not encoded here.
 */
export const OPT_OUT_KEY = "siis_analytics_opt_out";
export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type Attribution = Partial<Record<(typeof UTM_KEYS)[number], string>> & {
  referrer_domain?: string;
  qr_code_id?: string;
  campaign?: string;
};

const clip = (v: string | null, n: number) => (v ? v.trim().slice(0, n) || undefined : undefined);

/** Arrival attribution from the landing URL and referrer. Own-site referrers are ignored. */
export const captureAttribution = (url: string, referrer: string, ownHost: string): Attribution => {
  const u = new URL(url);
  const out: Attribution = {};
  for (const k of UTM_KEYS) {
    const v = clip(u.searchParams.get(k), 100);
    if (v) out[k] = v;
  }
  const qr = u.searchParams.get("qr");
  if (qr && /^[0-9a-f-]{36}$/i.test(qr)) out.qr_code_id = qr.toLowerCase();
  const campaign = clip(u.searchParams.get("campaign") ?? out.utm_campaign ?? null, 60);
  if (campaign) out.campaign = campaign.toLowerCase();
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, "");
      if (host && host !== ownHost.toLowerCase().replace(/^www\./, "")) out.referrer_domain = host.slice(0, 100);
    } catch { /* ignore malformed referrer */ }
  }
  return out;
};

/** Session attribution: a new non-direct touch replaces the old one; direct visits keep it. */
export const mergeSessionAttribution = (current: Attribution | null, arrival: Attribution): Attribution =>
  Object.keys(arrival).length ? arrival : current ?? {};

export const pathOnly = (path: string) => path.split("?")[0].split("#")[0].slice(0, 200);

export const deviceCategory = (width: number): "mobile" | "tablet" | "desktop" =>
  width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";

export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const isSessionExpired = (lastActivity: number | null, now: number) =>
  lastActivity === null || now - lastActivity > SESSION_IDLE_MS;

export const VISITOR_MAX_AGE_MS = 395 * 24 * 60 * 60 * 1000; // ~13 months
export const isVisitorExpired = (createdAt: number | null, now: number) =>
  createdAt === null || now - createdAt > VISITOR_MAX_AGE_MS;

export const isOptedOut = (opts: { optOutFlag: string | null; globalPrivacyControl?: boolean }) =>
  opts.optOutFlag === "1" || opts.globalPrivacyControl === true;

/** Whether this page view may send events at all once tracking is enabled. */
export const mayTrack = (opts: {
  enabled: boolean;
  pilotIds: readonly string[];
  athleteId?: string | null;
  optedOut: boolean;
  fixturePage: boolean;
}) => {
  if (!opts.enabled || opts.optedOut || opts.fixturePage) return false;
  if (opts.pilotIds.length === 0) return true; // site-wide phase
  return Boolean(opts.athleteId && opts.pilotIds.includes(opts.athleteId));
};
